import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { getToken } from '../../components/authentication/functions/authentication_management';
import {
  fetchResolvedFeatureFlags,
  getBlockingGroups as resolveBlockingGroups,
  isFeatureRuntimeEnabled as resolveFeatureRuntimeEnabled,
  parseFlagsFromAccessToken,
  resolveFeatureFlagGroups,
  type FeatureFlagGroupResolution,
  type ResolvedFlagMap,
} from './featureFlagService';

interface FeatureFlagContextType {
  flags: ResolvedFlagMap;
  groupResolution: FeatureFlagGroupResolution;
  isLoading: boolean;
  /** Strict: flag must exist and be `enabled: true` (matches backend contract). */
  isEnabled: (flagName: string) => boolean;
  isFeatureRuntimeEnabled: (flagName: string) => boolean;
  getBlockingGroups: (flagName: string) => string[];
  isOpenAllowed: (flagName: string) => boolean;
  getFlagConfig: (flagName: string) => Record<string, unknown>;
  refreshFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
  userId: string;
}

export function FeatureFlagProvider({ children, userId }: FeatureFlagProviderProps) {
  const [flags, setFlags] = useState<ResolvedFlagMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [warnedGroupUnknowns, setWarnedGroupUnknowns] = useState<Set<string>>(new Set());

  const loadFlags = useCallback(async () => {
    if (!userId) {
      setFlags({});
      setIsLoading(false);
      return;
    }

    const token = getToken();
    if (!token?.access_token) {
      setFlags({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fromJwt = parseFlagsFromAccessToken(token.access_token);
    setFlags(fromJwt);
    try {
      const fromApi = await fetchResolvedFeatureFlags();
      setFlags(fromApi);
    } catch {
      // Keep JWT-derived flags if /me fails (e.g. transient network).
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const isEnabled = useCallback((flagName: string): boolean => {
    return flags[flagName]?.enabled === true;
  }, [flags]);

  const groupResolution = React.useMemo(() => resolveFeatureFlagGroups(flags), [flags]);

  const isFeatureRuntimeEnabled = useCallback((flagName: string): boolean => {
    return resolveFeatureRuntimeEnabled(flags, flagName);
  }, [flags]);

  const getBlockingGroups = useCallback((flagName: string): string[] => {
    return resolveBlockingGroups(flags, flagName);
  }, [flags]);

  const isOpenAllowed = useCallback((flagName: string): boolean => {
    return isFeatureRuntimeEnabled(flagName);
  }, [isFeatureRuntimeEnabled]);

  const getFlagConfig = useCallback(
    (flagName: string): Record<string, unknown> => {
      return flags[flagName]?.config ?? {};
    },
    [flags]
  );

  const refreshFlags = useCallback(async () => {
    await loadFlags();
  }, [loadFlags]);

  useEffect(() => {
    const unknowns = groupResolution.unknownMembersByGroup;
    const nextWarned = new Set(warnedGroupUnknowns);
    let changed = false;

    for (const [groupName, members] of Object.entries(unknowns)) {
      if (members.length === 0) continue;
      const signature = `${groupName}:${members.join(',')}`;
      if (nextWarned.has(signature)) continue;
      nextWarned.add(signature);
      changed = true;
      console.warn(
        `[feature-flags] Group '${groupName}' references unknown members: ${members.join(', ')}`
      );
    }

    if (changed) {
      setWarnedGroupUnknowns(nextWarned);
    }
  }, [groupResolution.unknownMembersByGroup, warnedGroupUnknowns]);

  return (
    <FeatureFlagContext.Provider
      value={{
        flags,
        groupResolution,
        isLoading,
        isEnabled,
        isFeatureRuntimeEnabled,
        getBlockingGroups,
        isOpenAllowed,
        getFlagConfig,
        refreshFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useOptionalFeatureFlagContext(): FeatureFlagContextType | undefined {
  return useContext(FeatureFlagContext);
}

export function useFeatureFlagContext(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }
  return context;
}
